<template>
  <b-card class="change-password-form" :title="header" :sub-title="subheader">
    <b-form class="form" onkeydown="return event.key != 'Enter'">
      <b-form-group v-if="currentStep === steps['verify'].value">
        <custom-input
          ref="password"
          type="password"
          placeholder="Password"
          v-model="form.password"
          autofocus
          required
          :disabled="form.$busy"
          :is-invalid="form.$getError('password')"
          @keyup.enter="nextStep"
        ></custom-input>
      </b-form-group>
      <b-form-group v-else>
        <custom-input
          ref="new_password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="New Password"
          v-model="form.new_password"
          required
          :disabled="form.$busy"
          :is-invalid="form.$getError('new_password')"
          @keyup.enter="nextStep"
        >
          <template slot="icon">
            <b-icon
              v-if="!showPassword"
              class="show"
              icon="eye"
              aria-hidden="true"
              @click="togglePassword(true)"
            ></b-icon>
            <b-icon
              v-else
              class="hide"
              icon="eye-slash"
              aria-hidden="true"
              @click="togglePassword(false)"
            ></b-icon>
          </template>
        </custom-input>
      </b-form-group>

      <error-message
        class="mb-2 mt-3"
        sign=""
        :error="
          form.$getError('password') ||
            form.$getError('new_password') ||
            form.$errorMessage
        "
      ></error-message>

      <hr class="card-divider" />

      <div class="modal__footer">
        <p class="forgot-btn mt-3" @click="$refs.forgot.show()">
          Forgot Password?
        </p>
        <forgot-password-form ref="forgot"></forgot-password-form>

        <div class="modal__btns">
          <b-button
            variant="outline-secondary"
            @click="goTo('user.settings-page', { tab: 3 })"
            >Discard</b-button
          >
          <b-button variant="primary" :disabled="form.$busy" @click="nextStep">
            <b-spinner small v-if="form.$busy"></b-spinner>&nbsp;{{
              buttonText
            }}
          </b-button>
        </div>
      </div>
    </b-form>
  </b-card>
</template>
<script>
/**
 * ==================================================================================
 * Change password form
 * ==================================================================================
 **/

import { mapState, mapActions } from 'vuex'
import Form from '@/utils/form'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'
import ForgotPasswordForm from '@/components/forms/ForgotPasswordForm.vue'

export default {
  components: {
    CustomInput,
    ErrorMessage,
    ForgotPasswordForm
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),

    onChangePassword() {
      if (!this.user) return
      return this.user.has_password ? this.updatePassword : this.setPassword
    },

    onChangePasswordParam() {
      if (!this.user) return
      return this.user.has_password
        ? {
            current_password: this.form.password,
            new_password: this.form.new_password,
            new_password_confirmation: this.form.new_password
          }
        : {
            password: this.form.new_password,
            password_confirmation: this.form.new_password
          }
    }
  },
  watch: {
    user(newVal) {
      this.initStep()
    }
  },
  data: function() {
    return {
      showPassword: false,
      form: new Form({
        password: null,
        new_password: null
      }),

      header: null,
      subheader: null,
      buttonText: null,
      currentStep: null,
      steps: {
        verify: {
          value: 1,
          header: 'Verify Password',
          subheader: 'Enter your current password to change your password',
          buttonText: 'Continue'
        },
        new: {
          value: 2,
          header: 'New Password',
          subheader: 'Enter your new password',
          buttonText: 'Change password'
        }
      }
    }
  },
  methods: {
    ...mapActions({
      checkPassword: 'auth/checkPassword',
      updatePassword: 'auth/updatePassword',
      setPassword: 'auth/setPassword'
    }),
    initStep() {
      if (this.user) {
        this.setStep(this.steps[this.user.has_password ? 'verify' : 'new'])
      }
    },

    nextStep() {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      switch (this.currentStep) {
        case this.steps['verify'].value:
          this.verifyPassword()
          break
        case this.steps['new'].value:
          this.changePassword()
          break
      }
    },

    async verifyPassword() {
      await this.checkPassword({
        password: this.form.password
      })
        .then(() => {
          this.form.$setLoading(false)
          this.setStep(this.steps['new'])
          this.setFocus('new_password')
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.setFocus('password')
        })
    },

    async changePassword() {
      await this.onChangePassword(this.onChangePasswordParam)
        .then(() => {
          this.$router.push({
            name: 'user.settings-page',
            params: {
              tab: 3,
              alert: {
                label: 'Your password has been updated'
              }
            }
          })
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.setFocus('new_password')
        })
    },

    togglePassword(bool) {
      this.showPassword = bool
    },

    setStep(obj) {
      this.currentStep = obj.value
      this.header = obj.header
      this.subheader = obj.subheader
      this.buttonText = obj.buttonText
    },

    setFocus(ref) {
      this.$nextTick(() => {
        if (!this.$refs[ref]) return
        this.$refs[ref].focus()
      })
    }
  },

  created() {
    this.initStep()
  }
}
</script>
<style scoped lang="scss">
.change-password-form {
}
</style>
